import { useEffect } from 'react';

interface Props {
  photoKeys: string[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export function PhotoLightbox({ photoKeys, index, onClose, onPrev, onNext }: Props) {
  // Keyboard nav: arrows + escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  if (index < 0 || index >= photoKeys.length) return null;
  const key = photoKeys[index];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Resident photo viewer"
      className="fixed inset-0 z-[100] bg-forest/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        className="absolute top-4 right-4 text-cream/80 hover:text-cream text-3xl leading-none w-10 h-10 flex items-center justify-center"
      >
        ×
      </button>

      {/* Prev */}
      {index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="Previous photo"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/70 hover:text-cream text-4xl leading-none w-12 h-12 flex items-center justify-center"
        >
          ‹
        </button>
      )}

      {/* Next */}
      {index < photoKeys.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="Next photo"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/70 hover:text-cream text-4xl leading-none w-12 h-12 flex items-center justify-center"
        >
          ›
        </button>
      )}

      {/* Image */}
      <img
        src={`/api/admin/photo/${key}`}
        alt="Resident yard"
        className="max-w-[90vw] max-h-[85vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Counter */}
      <div className="absolute bottom-4 left-0 right-0 text-center text-cream/70 text-xs tracking-[0.2em] uppercase">
        {index + 1} / {photoKeys.length}
      </div>
    </div>
  );
}
